class VideoPlayPauseButton extends HTMLElement {
  static YT_PLAY = '{"event":"command","func":"playVideo","args":""}';
  static YT_PAUSE = '{"event":"command","func":"pauseVideo","args":""}';

  /* --------------- *** Constructor & bound handlers *** --------------- */

  constructor() {
    super();

    this._html5Cleanup = null;
    this._swiperUnsubscribe = null;
    this._swiperAttachInterval = null;
    this._onWindowLoad = null;
    this._boundHandleClick = this.handleClick.bind(this);
    this._boundHandleKeyDown = this.handleKeyDown.bind(this);
  }

  /* --------------- *** Custom element lifecycle *** --------------- */

  connectedCallback() {
    if (this._lifecycleConnected) return;

    this._lifecycleConnected = true;

    if (!this.hasAttribute("role")) {
      this.setAttribute("role", "button");
    }

    if (!this.hasAttribute("tabindex")) {
      this.setAttribute("tabindex", "0");
    }

    this.setup();
  }

  disconnectedCallback() {
    this.teardown();
    this._lifecycleConnected = false;
  }

  /* --------------- *** Initial wiring & cleanup *** --------------- */

  setup() {
    const mode = this.dataset.videoMode || "active_slide";
    const media = this.resolveMedia();

    if (mode === "active_slide") {
      if (!this.isHeroPlayPauseControl()) {
        this.hidden = !media;
      } else {
        this.hidden = false;
      }

      if (media) {
        this.dataset.iframePlaying = "true";

        if (this.isHtml5Video(media)) {
          this.bindHtml5(media);
          this.setUiPaused(media.paused);
        } else {
          this.setUiPaused(false);
        }
      }

      this.attachSwiperSync();
      this.addViewportListeners();
      this.addEventListener("click", this._boundHandleClick);
      this.addEventListener("keydown", this._boundHandleKeyDown);

      return;
    }

    /* Listeners must always attach: scrolling tiles (and similar) often have no
       media on slide 0; setup returns before rebindMedia runs — otherwise
       clicks never wire up. */
    if (!media) {
      this.hidden = true;
    } else {
      this.hidden = false;
      this.dataset.iframePlaying = "true";

      if (this.isHtml5Video(media)) {
        this.bindHtml5(media);
        this.setUiPaused(media.paused);
      } else {
        this.setUiPaused(false);
      }
    }

    this.addViewportListeners();
    this.addEventListener("click", this._boundHandleClick);
    this.addEventListener("keydown", this._boundHandleKeyDown);
  }

  /**
   * Re-resolve media (e.g. after `data-media-target` changes for active slide).
   * Target mode only; no-ops for active_slide / Swiper.
   */
  rebindMedia() {
    const mode = this.dataset.videoMode || "active_slide";

    if (mode !== "target") return;

    if (this._html5Cleanup) {
      this._html5Cleanup();
      this._html5Cleanup = null;
    }

    const media = this.resolveMedia();

    if (!media) {
      this.hidden = true;

      return;
    }

    this.hidden = false;
    this.dataset.iframePlaying = "true";

    if (this.isHtml5Video(media)) {
      this.bindHtml5(media);
      this.setUiPaused(media.paused);
    } else {
      this.setUiPaused(false);
    }
  }

  teardown() {
    this.removeEventListener("click", this._boundHandleClick);
    this.removeEventListener("keydown", this._boundHandleKeyDown);

    if (this._html5Cleanup) {
      this._html5Cleanup();
      this._html5Cleanup = null;
    }

    if (this._swiperUnsubscribe) {
      this._swiperUnsubscribe();
      this._swiperUnsubscribe = null;
    }

    if (this._swiperAttachInterval) {
      clearInterval(this._swiperAttachInterval);
      this._swiperAttachInterval = null;
    }

    if (this._onWindowLoad) {
      window.removeEventListener("load", this._onWindowLoad);
      this._onWindowLoad = null;
    }

    this.removeViewportListeners();
  }

  /* --------------- *** Viewport breakpoint listeners *** --------------- */

  addViewportListeners() {
    this._viewportCleanups = [];

    const breakpoints = [750, 990];

    for (const bp of breakpoints) {
      const mq = window.matchMedia(`(min-width: ${bp}px)`);
      const handler = () => this.handleViewportChange();

      mq.addEventListener("change", handler);
      this._viewportCleanups.push(() =>
        mq.removeEventListener("change", handler),
      );
    }
  }

  removeViewportListeners() {
    if (this._viewportCleanups) {
      this._viewportCleanups.forEach((fn) => fn());
      this._viewportCleanups = null;
    }
  }

  handleViewportChange() {
    const wasPlaying = !this.classList.contains(
      "video-play-pause-button--paused",
    );

    if (this._html5Cleanup) {
      this._html5Cleanup();
      this._html5Cleanup = null;
    }

    const media = this.resolveMedia();

    if (!media) {
      if (!this.isHeroPlayPauseControl()) {
        this.hidden = true;
      }

      return;
    }

    if (!this.isHeroPlayPauseControl()) {
      this.hidden = false;
    }

    if (this.isHtml5Video(media)) {
      this.bindHtml5(media);

      if (wasPlaying && media.paused) {
        const p = media.play();

        if (p && typeof p.then === "function") {
          p.catch(() => {});
        }
      } else if (!wasPlaying && !media.paused) {
        media.pause();
      }

      this.setUiPaused(media.paused);
    } else {
      this.dataset.iframePlaying = wasPlaying ? "true" : "false";

      if (wasPlaying) {
        this.playIframe(media);
      } else {
        this.pauseIframe(media);
      }

      this.setUiPaused(!wasPlaying);
    }
  }

  /* --------------- *** Visibility & element kind *** --------------- */

  isVisible(el) {
    if (!el || !el.getBoundingClientRect) return false;

    let node = el;

    while (node && node !== document.body) {
      const cs = getComputedStyle(node);

      if (
        cs.display === "none" ||
        cs.visibility === "hidden" ||
        cs.opacity === "0"
      ) {
        return false;
      }

      node = node.parentElement;
    }

    return true;
  }

  isHtml5Video(el) {
    return Boolean(el && el.tagName === "VIDEO");
  }

  isHeroPlayPauseControl() {
    return this.classList.contains("hero__video-play-pause");
  }

  /* --------------- *** Embedded iframe detection & control *** --------------- */

  getYoutubeIframe(el) {
    return el?.classList?.contains("js-youtube") ? el : null;
  }

  getVimeoIframe(el) {
    return el?.classList?.contains("js-vimeo") ? el : null;
  }

  pauseIframe(iframe) {
    if (this.getYoutubeIframe(iframe)) {
      iframe.contentWindow?.postMessage(VideoPlayPauseButton.YT_PAUSE, "*");

      return;
    }

    if (this.getVimeoIframe(iframe)) {
      iframe.contentWindow?.postMessage('{"method":"pause"}', "*");
    }
  }

  playIframe(iframe) {
    if (this.getYoutubeIframe(iframe)) {
      iframe.contentWindow?.postMessage(VideoPlayPauseButton.YT_PLAY, "*");

      return;
    }

    if (this.getVimeoIframe(iframe)) {
      iframe.contentWindow?.postMessage('{"method":"play"}', "*");
    }
  }

  /* --------------- *** Which video/iframe to control *** --------------- */
  // Picks the first visible media node in a slide, else the first match.
  queryMediaInSlide(slide) {
    if (!slide) return null;

    const nodes = slide.querySelectorAll(
      "video, iframe.js-youtube, iframe.js-vimeo",
    );

    for (const node of nodes) {
      if (this.isVisible(node)) return node;
    }

    return nodes[0] || null;
  }

  resolveSlideHost() {
    const sel = this.dataset.slideHost?.trim();

    if (sel) {
      const found = this.closest(sel);

      if (found) return found;
    }

    return this.closest("hero-slider, .swiper");
  }

  // Resolves `target` (CSS selector) vs `active_slide` (Swiper) mode.
  resolveMedia() {
    const mode = this.dataset.videoMode || "active_slide";

    if (mode === "target") {
      const raw = this.dataset.mediaTarget?.trim();

      if (!raw) return null;

      let nodes;

      try {
        nodes = document.querySelectorAll(raw);
      } catch {
        return null;
      }

      for (const node of nodes) {
        if (this.isVisible(node)) return node;
      }

      return nodes[0] || null;
    }

    const host = this.resolveSlideHost();

    if (!host) return null;

    const slide = host.querySelector(".swiper-slide-active");

    if (slide) return this.queryMediaInSlide(slide);

    return this.queryMediaInSlide(host);
  }

  /* --------------- *** Paused UI & HTML5 sync *** --------------- */

  setUiPaused(paused) {
    const pauseText = this.dataset.labelPause || "Pause video";
    const playText = this.dataset.labelPlay || "Play video";

    this.classList.toggle("video-play-pause-button--paused", paused);
    this.setAttribute("aria-pressed", paused ? "false" : "true");
    this.setAttribute("aria-label", paused ? playText : pauseText);
  }

  bindHtml5(video) {
    if (this._html5Cleanup) {
      this._html5Cleanup();
      this._html5Cleanup = null;
    }

    const onPlay = () => {
      this.setUiPaused(false);
    };

    const onPause = () => {
      this.setUiPaused(true);
    };

    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);

    this._html5Cleanup = () => {
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
    };
  }

  /* --------------- *** Swiper: re-bind when active slide changes *** --------------- */
  // Polls until Swiper exists, then listens for slide transitions.
  attachSwiperSync() {
    const tryAttach = () => {
      const host = this.resolveSlideHost();

      if (!host) return false;

      const swiperEl = host.classList?.contains("swiper")
        ? host
        : host.querySelector?.(".swiper") || host;
      const swiper = swiperEl?.swiper;

      if (!swiper || typeof swiper.on !== "function") return false;

      if (this._swiperUnsubscribe) {
        this._swiperUnsubscribe();
        this._swiperUnsubscribe = null;
      }

      const onSlideChange = () => {
        if (this._html5Cleanup) {
          this._html5Cleanup();
          this._html5Cleanup = null;
        }

        this.dataset.iframePlaying = "true";

        const media = this.resolveMedia();

        if (!media) {
          if (!this.isHeroPlayPauseControl()) {
            this.hidden = true;
          }

          return;
        }

        if (!this.isHeroPlayPauseControl()) {
          this.hidden = false;
        }

        if (this.isHtml5Video(media)) {
          this.bindHtml5(media);
          this.setUiPaused(media.paused);
        } else {
          this.setUiPaused(false);
        }
      };

      swiper.on("slideChangeTransitionEnd", onSlideChange);

      this._swiperUnsubscribe = () => {
        if (typeof swiper.off === "function") {
          swiper.off("slideChangeTransitionEnd", onSlideChange);
        }
      };

      if (this._onWindowLoad) {
        window.removeEventListener("load", this._onWindowLoad);
        this._onWindowLoad = null;
      }

      return true;
    };

    if (tryAttach()) return;

    let attempts = 0;
    const maxAttempts = 80;

    this._swiperAttachInterval = setInterval(() => {
      attempts += 1;

      if (tryAttach() || attempts >= maxAttempts) {
        clearInterval(this._swiperAttachInterval);
        this._swiperAttachInterval = null;
      }
    }, 100);

    this._onWindowLoad = () => {
      tryAttach();
    };

    window.addEventListener("load", this._onWindowLoad, { once: true });
  }

  /* --------------- *** User interaction *** --------------- */

  handleClick(event) {
    event.preventDefault();

    if (this.hasAttribute("disabled")) return;

    const media = this.resolveMedia();

    if (!media) return;

    if (this.isHtml5Video(media)) {
      if (media.paused) {
        const p = media.play();

        if (p && typeof p.then === "function") {
          p.catch(() => {});
        }

        this.dataset.iframePlaying = "true";
      } else {
        media.pause();
      }

      this.setUiPaused(media.paused);

      return;
    }

    if (this.getYoutubeIframe(media) || this.getVimeoIframe(media)) {
      const playing = this.dataset.iframePlaying !== "false";

      if (playing) {
        this.pauseIframe(media);
        this.dataset.iframePlaying = "false";
        this.setUiPaused(true);
      } else {
        this.playIframe(media);
        this.dataset.iframePlaying = "true";
        this.setUiPaused(false);
      }
    }
  }

  handleKeyDown(event) {
    if (event.key !== "Enter" && event.key !== " ") return;

    if (this.hasAttribute("disabled")) return;

    event.preventDefault();
    this.handleClick(event);
  }
}

customElements.define("video-play-pause-button", VideoPlayPauseButton);
